import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, Stagger, StaggerItem } from "./Reveal";
import { Overline } from "./Section";
import { CLINIC_PHOTOS, STOCK } from "../data/site";

const CHAPTERS = [
  { n: "01", title: "Individualized Treatment", text: "Every care plan is shaped around your specific needs, goals and comfort — never one-size-fits-all." },
  { n: "02", title: "Physiotherapy & Rehabilitation", text: "From pain relief to post-operative and neurological rehabilitation, we help restore movement and strength." },
  { n: "03", title: "Speech & Hearing Care", text: "Professional speech, language and hearing services with individualized assessment and therapy." },
  { n: "04", title: "For Children & Adults", text: "Compassionate, patient-centered care for every age, delivered in a calm and welcoming space." },
];

export const AboutSection = ({ withCta = true }) => (
  <section className="py-24 md:py-32 bg-white" data-testid="about-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-start">
      <div className="lg:col-span-5 lg:sticky lg:top-28">
        <Overline>About the Clinic</Overline>
        <Reveal delay={0.05}>
          <h2 className="mt-5 font-display text-4xl md:text-5xl font-bold text-dash-ink leading-[1.1]">
            Care That Helps You Move, Communicate &amp; Live Better
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-dash-muted leading-relaxed">
            DASH Physiotherapy &amp; Speech and Hearing Clinic brings specialized physiotherapy, rehabilitation, speech therapy and hearing care together under one roof in Belpahar, Odisha. Our approach is built around individualized treatment, professional care and patient comfort — for both children and adults.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-4 text-dash-muted leading-relaxed">
            Whether you're recovering from an injury, managing pain, or seeking support with speech, language or hearing, our team is here to guide you with attentive, compassionate care.
          </p>
        </Reveal>

        <div className="mt-8 grid grid-cols-2 gap-4">
          <div className="overflow-hidden rounded-2xl"><img src={CLINIC_PHOTOS.reception} alt="Clinic reception" className="h-40 w-full object-cover" /></div>
          <div className="overflow-hidden rounded-2xl mt-6"><img src={STOCK.physioWrist} alt="Physiotherapy care" className="h-40 w-full object-cover" /></div>
        </div>

        {withCta && (
          <Reveal delay={0.2}>
            <Link to="/about" data-testid="about-more-btn" className="mt-8 inline-flex items-center gap-2 rounded-full border border-dash-line px-6 py-3.5 font-semibold text-dash-ink transition-all hover:border-dash-teal hover:text-dash-teal">
              More About Us <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        )}
      </div>

      <div className="lg:col-span-7">
        <Stagger className="space-y-5">
          {CHAPTERS.map((c) => (
            <StaggerItem key={c.n}>
              <div className="group flex gap-6 rounded-3xl border border-dash-line bg-dash-bg p-7 md:p-9 transition-all duration-400 hover:border-dash-teal/40 hover:-translate-y-1">
                <span className="font-display text-4xl md:text-5xl font-bold text-dash-teal/25 transition-colors group-hover:text-dash-teal">{c.n}</span>
                <div>
                  <h3 className="font-display text-xl md:text-2xl font-bold text-dash-ink">{c.title}</h3>
                  <p className="mt-2 text-dash-muted leading-relaxed">{c.text}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </div>
  </section>
);
